import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronRight,
  MapPin,
  Pencil,
  Phone,
  PhoneForwarded,
  ShieldOff,
  Users2,
  Workflow,
  type LucideIcon,
} from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import { OverviewToolbar } from "../components/layout/OverviewToolbar";
import {
  loadViewMode,
  saveViewMode,
  useFitsMultipleColumns,
  ViewToggle,
  type ViewMode,
} from "../components/layout/ViewToggle";
import {
  FeatureTiles,
  type FeatureTileItem,
} from "../components/layout/FeatureTiles";
import { CountryFlag } from "../components/CountryFlag";
import {
  numbersApi,
  SCRIPT_TYPE_IDS,
  type NumberListItem,
} from "../api/numbersApi";
import { formatPhoneNumber } from "../lib/phone";

const NUMBERS_CARD_MIN = 280;

type LoadState =
  | { status: "loading" }
  | { status: "ready"; items: NumberListItem[] }
  | { status: "error"; message: string };

/**
 * Customer numbers overview. Lists every active DNIS the customer owns
 * with the application currently routed to it and the per-call/per-minute
 * tariff. Above the list we expose a row of "feature tiles" so customers
 * can hop into related sub-features (Blacklist today, more later) without
 * us pushing extra entries into the sidebar.
 */
export function NumbersPage() {
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [search, setSearch] = useState("");
  const [view, setView] = useState<ViewMode>(() => loadViewMode("numbers"));
  const navigate = useNavigate();
  const listRef = useRef<HTMLDivElement>(null);
  const canShowToggle = useFitsMultipleColumns(listRef, NUMBERS_CARD_MIN);

  const load = useCallback(() => {
    let active = true;
    setState({ status: "loading" });
    numbersApi
      .list()
      .then((response) => {
        if (!active) return;
        setState({ status: "ready", items: response.items ?? [] });
      })
      .catch((err: unknown) => {
        if (!active) return;
        const message =
          err instanceof Error ? err.message : "Failed to load numbers";
        setState({ status: "error", message });
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => load(), [load]);

  const filtered = useMemo(() => {
    if (state.status !== "ready") return [] as NumberListItem[];
    const q = search.trim().toLowerCase();
    if (!q) return state.items;
    return state.items.filter((n) => {
      const formatted = formatPhoneNumber(
        n.number,
        n.countryCode,
        n.areaCode,
      ).toLowerCase();
      return (
        formatted.includes(q) ||
        (n.number ?? "").toLowerCase().includes(q) ||
        (n.application ?? "").toLowerCase().includes(q) ||
        (n.applicationLabel ?? "").toLowerCase().includes(q) ||
        (n.scriptName ?? "").toLowerCase().includes(q) ||
        (n.numberType ?? "").toLowerCase().includes(q) ||
        (n.countryName ?? "").toLowerCase().includes(q) ||
        (n.areaCity ?? "").toLowerCase().includes(q) ||
        (n.areaState ?? "").toLowerCase().includes(q) ||
        (n.areaCode ?? "").toLowerCase().includes(q)
      );
    });
  }, [state, search]);

  // Build a single, human-readable "area" label from the bits that the
  // API returned. CountryArea rows are inconsistently filled (some have
  // a city, some only a state, some only an area-code), so we use the
  // most specific value available.
  // The API resolves the area label server-side via vAivrmanagerNumber
  // (city > stateProv > areaType > NULL), so the SPA just consumes it.
  const areaLabel = (n: NumberListItem): string => n.areaLabel || "";

  // Same applies to the "application" column: scriptTypeId picks the
  // icon, applicationLabel is server-resolved (linked destinations,
  // huntgroup names, active flow application, …) with a sensible
  // fallback to the legacy scriptName for "Custom" rows.
  const applicationCell = (n: NumberListItem): {
    icon: ReactNode;
    label: string;
    title: string;
  } | null => {
    const label = (n.applicationLabel || "").trim() || (n.scriptName || "").trim();
    if (!label) return null;
    const Icon = pickApplicationIcon(n.scriptTypeId);
    return {
      icon: <Icon size={14} />,
      label,
      title: label,
    };
  };

  const features: FeatureTileItem[] = [
    {
      to: "/numbers/blacklists",
      label: "Blacklist",
      description: "Block individual callers from reaching your numbers",
      icon: <ShieldOff size={20} />,
    },
  ];

  return (
    <>
      <PageHeader title="Numbers" />
      <div className="dashboard">
        <FeatureTiles items={features} />

        {state.status === "loading" && (
          <div className="tile" style={{ color: "var(--portal-muted)" }}>
            Loading numbers…
          </div>
        )}
        {state.status === "error" && (
          <div
            className="tile"
            style={{
              borderColor: "#e74c3c",
              color: "#c0392b",
              background: "#fdecea",
              gap: 10,
            }}
          >
            <strong>Something went wrong.</strong>
            <div style={{ fontSize: "0.9rem" }}>{state.message}</div>
            <button
              type="button"
              onClick={load}
              style={{
                alignSelf: "flex-start",
                padding: "6px 14px",
                borderRadius: 8,
                border: "1px solid #e74c3c",
                background: "transparent",
                color: "#c0392b",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Try again
            </button>
          </div>
        )}
        {state.status === "ready" && state.items.length === 0 && <EmptyState />}
        {state.status === "ready" && state.items.length > 0 && (
          <>
            <OverviewToolbar
              search={search}
              onSearch={setSearch}
              placeholder="Search by number, application, type, country or area"
              count={{ matched: filtered.length, total: state.items.length }}
              trailing={
                canShowToggle ? (
                  <ViewToggle
                    value={view}
                    onChange={(next) => {
                      setView(next);
                      saveViewMode("numbers", next);
                    }}
                  />
                ) : null
              }
            />
            <div ref={listRef}>
              {filtered.length === 0 ? (
                <div className="tile" style={{ color: "var(--portal-muted)" }}>
                  No numbers match "{search}".
                </div>
              ) : view === "card" || !canShowToggle ? (
                <NumbersCardGrid
                  items={filtered}
                  onOpen={(id) => navigate(`/numbers/${id}`)}
                  areaLabel={areaLabel}
                  applicationCell={applicationCell}
                />
              ) : (
                <NumbersRowList
                  items={filtered}
                  onOpen={(id) => navigate(`/numbers/${id}`)}
                  areaLabel={areaLabel}
                  applicationCell={applicationCell}
                />
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

function EmptyState() {
  return (
    <section
      className="tile"
      style={{
        maxWidth: 720,
        display: "grid",
        gridTemplateColumns: "56px 1fr",
        gap: 18,
        alignItems: "flex-start",
        padding: 24,
      }}
    >
      <div
        className="tile-row-icon"
        style={{ width: 56, height: 56, borderRadius: 14 }}
        aria-hidden="true"
      >
        <Phone size={28} />
      </div>
      <div>
        <h3 style={{ marginTop: 0 }}>No numbers yet</h3>
        <p style={{ marginTop: 0 }}>
          There are no active numbers for this customer at the moment.
        </p>
      </div>
    </section>
  );
}

function NumbersCardGrid({
  items,
  onOpen,
  areaLabel,
  applicationCell,
}: {
  items: NumberListItem[];
  onOpen: (dnisId: number) => void;
  areaLabel: (n: NumberListItem) => string;
  applicationCell: (n: NumberListItem) => ApplicationCellInfo | null;
}) {
  return (
    <div
      className="tile-cards"
      style={{ ["--tile-min" as never]: `${NUMBERS_CARD_MIN}px` }}
    >
      {items.map((item) => {
        const area = areaLabel(item);
        const app = applicationCell(item);
        const formatted = formatPhoneNumber(
          item.number,
          item.countryCode,
          item.areaCode,
        );
        return (
          <button
            key={item.dnisId}
            type="button"
            className="tile tile-link"
            onClick={() => onOpen(item.dnisId)}
            style={{
              textAlign: "left",
              gap: 10,
              padding: 20,
              cursor: "pointer",
            }}
            aria-label={`Open ${formatted || item.number}`}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <div
                style={{
                  fontWeight: 700,
                  fontSize: "1rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={item.number}
              >
                {formatted || "—"}
              </div>
              <NumberTypeChip type={item.numberType} />
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                color: "var(--portal-muted)",
                fontSize: "0.9rem",
                minHeight: 22,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={item.countryName || "Country unknown"}
            >
              <MetaIcon>
                <CountryFlag
                  iso2={item.countryIso2}
                  title={item.countryName}
                  size={14}
                />
              </MetaIcon>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                {item.countryName || "Unknown country"}
              </span>
            </div>
            {area && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  color: "var(--portal-muted)",
                  fontSize: "0.9rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={area}
              >
                <MetaIcon>
                  <MapPin size={14} />
                </MetaIcon>
                {area}
              </div>
            )}
            {app && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  color: "var(--portal-muted)",
                  fontSize: "0.9rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={app.title}
              >
                <MetaIcon>{app.icon}</MetaIcon>
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {app.label}
                </span>
              </div>
            )}
            <div
              style={{
                marginTop: "auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                color: "var(--portal-primary)",
                fontWeight: 600,
                fontSize: "0.85rem",
              }}
            >
              Open <ChevronRight size={16} />
            </div>
          </button>
        );
      })}
    </div>
  );
}

function NumbersRowList({
  items,
  onOpen,
  areaLabel,
  applicationCell,
}: {
  items: NumberListItem[];
  onOpen: (dnisId: number) => void;
  areaLabel: (n: NumberListItem) => string;
  applicationCell: (n: NumberListItem) => ApplicationCellInfo | null;
}) {
  return (
    <div className="tile-rows">
      {items.map((item) => {
        const area = areaLabel(item);
        const app = applicationCell(item);
        const formatted = formatPhoneNumber(
          item.number,
          item.countryCode,
          item.areaCode,
        );
        return (
          <button
            key={item.dnisId}
            type="button"
            className="tile tile-link"
            onClick={() => onOpen(item.dnisId)}
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 16,
              padding: "12px 18px",
              textAlign: "left",
              cursor: "pointer",
            }}
            aria-label={`Open ${formatted || item.number}`}
          >
            <div
              style={{
                flex: "1 1 0",
                minWidth: 0,
                fontWeight: 600,
                fontSize: "1rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={item.number}
            >
              {formatted || "—"}
            </div>
            <div style={{ flexShrink: 0 }}>
              <NumberTypeChip type={item.numberType} />
            </div>
            <div
              className="tile-row-detail"
              style={{
                flex: "1 1 0",
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                gap: 8,
                color: "var(--portal-muted)",
                fontSize: "0.9rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={
                area
                  ? `${item.countryName || "Country unknown"} · ${area}`
                  : item.countryName || "Country unknown"
              }
            >
              <MetaIcon>
                <CountryFlag
                  iso2={item.countryIso2}
                  title={item.countryName}
                  size={14}
                />
              </MetaIcon>
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {item.countryName || "Unknown"}
                {area && (
                  <span style={{ color: "var(--portal-muted)" }}>
                    {" · "}
                    {area}
                  </span>
                )}
              </span>
            </div>
            <div
              className="tile-row-detail"
              style={{
                flex: "1 1 0",
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                gap: 8,
                color: "var(--portal-muted)",
                fontSize: "0.9rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={app?.title ?? ""}
            >
              <MetaIcon>{app?.icon ?? null}</MetaIcon>
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {app?.label ?? "—"}
              </span>
            </div>
            <Pencil
              size={16}
              style={{ color: "var(--portal-primary)", flexShrink: 0 }}
            />
          </button>
        );
      })}
    </div>
  );
}

/**
 * Fixed-width slot used in front of every meta line ("flag · country",
 * "pin · city", …) so the text after the icon always lands on the same
 * X-coordinate regardless of whether the icon is a flag emoji (varies
 * per font/platform) or a lucide stroke-icon.
 */
const META_ICON_SLOT = 22;

function MetaIcon({ children }: { children: ReactNode }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: META_ICON_SLOT,
        flexShrink: 0,
        // Lock the line to the icon's natural baseline so flags don't
        // push the row's vertical rhythm off compared to stroke icons.
        lineHeight: 1,
      }}
    >
      {children}
    </span>
  );
}

type ApplicationCellInfo = {
  icon: ReactNode;
  label: string;
  title: string;
};

/**
 * Maps the smxSystems scriptTypeId to the same lucide glyph the sidebar
 * uses for the corresponding feature, so the application column on the
 * Numbers list reads the same vocabulary as the navigation:
 * Destination → "Destinations" sidebar icon (PhoneForwarded), Huntgroup
 * → "Huntgroups" icon (Users2), FlowBuilder + every "Custom" script →
 * "Applications" icon (Workflow).
 */
function pickApplicationIcon(scriptTypeId: number): LucideIcon {
  switch (scriptTypeId) {
    case SCRIPT_TYPE_IDS.destination:
      return PhoneForwarded;
    case SCRIPT_TYPE_IDS.huntgroup:
      return Users2;
    default:
      // FlowBuilder and any "Custom" script (sipTrunk, legacy
      // scripts, …) all fall under the generic "Applications" bucket
      // in the navigation, so we reuse the Workflow icon for both.
      return Workflow;
  }
}

/**
 * Pill that shows the number-type (Geographic, Service, …). Kept
 * deliberately neutral because the mix of types is large and we don't
 * have brand-coloured semantics for each one yet.
 */
function NumberTypeChip({ type }: { type: string }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: 999,
        border: "1px solid var(--portal-border)",
        background: "var(--portal-surface)",
        color: "var(--portal-muted)",
        fontSize: "0.8rem",
        fontWeight: 600,
      }}
    >
      {type || "Unknown"}
    </span>
  );
}
